import { auth } from "./firebase.class";
import { enquadramentoEmIngles } from "./enquadramento-em-ingles";

/**
 * O pedido foi RECUSADO pela barreira de conteúdo — não falhou.
 *
 * Tipo próprio porque a tela precisa tratar diferente: recusa não se repete, se reescreve. Quem
 * pegar um `Error` genérico aqui e disser "tente de novo" está mandando a criança bater de novo
 * na mesma porta.
 */
export class ConteudoRecusadoError extends Error {
  constructor(mensagem: string) {
    super(mensagem);
    this.name = "ConteudoRecusadoError";
    Object.setPrototypeOf(this, ConteudoRecusadoError.prototype);
  }
}

/**
 * T22 · TODAS as contas do klein falharam (o servidor diz 503). É o único caso em que a tela pode
 * desenhar pelo caminho antigo — e, quando desenha, avisa (0049 T3.2): *"você tem que ter um aviso
 * explicando o que aconteceu e porque aconteceu para que o público saiba que mudou o modelo"*.
 */
export class KleinEsgotadoError extends Error {
  constructor(mensagem: string) {
    super(mensagem);
    this.name = "KleinEsgotadoError";
    Object.setPrototypeOf(this, KleinEsgotadoError.prototype);
  }
}

export default class Imgen {
  /**
   * @param semente  A MESMA para todos os quadros de uma história, e é o que os mantém parecidos.
   *
   * `T5.4`: *"nada tem consistência (...) depois ele foi outra criança aleatória que não tem nada
   * a ver com o primeiro"*. Sem semente, cada quadro é um sorteio independente — mesmo prompt de
   * estética, mesmo modelo, personagem diferente.
   *
   * A capacidade existia na cadeia inteira e ninguém a usava: `generateImage` no backend tem o
   * parâmetro desde sempre, com o comentário "character consistency multi-panel", e o HuggingFace
   * o recebe. O que faltava eram as duas pontas — a rota HTTP, que passava `undefined`, e aqui.
   */
  static async generateImages(
    prompt: string,
    imageUrl?: string,
    semente?: number,
    tipo?: string | null,
    estilo?: string
  ): Promise<{ images: string[]; provider: string }> {
    // Backend agora tem cascade multi-provider (HF → Cloudflare → StableHorde → Pollinations → Placeholder).
    // Chaves por-usuario (Pixazo, Bing) foram removidas — providers descontinuados no backend.
    // ── O ENQUADRAMENTO ATRAVESSA EM INGLES — `T1.33`, `T6.3` ─────────────────────────────
    //
    // Medido em 29/09 contra producao, mesma cena e mesma semente, mudando SO a lingua da frase
    // de enquadramento: `angulo de camera alto, visto de cima para baixo` deu **0 de 3**, e
    // `high camera angle, top-down view` deu **3 de 3**. Na amostra de aderencia do mesmo dia, 24
    // dos 30 elementos pedidos apareceram e o unico que nunca apareceu foi o enquadramento.
    //
    // A TROCA E AQUI, E NAO NO BOTAO, de proposito: o botao escreve a frase DENTRO da caixa que a
    // crianca le e edita. Trocar la poria ingles na frente dela, que e a queixa do `T1.4`.
    const params = new URLSearchParams({ prompt: enquadramentoEmIngles(prompt) });
    if (imageUrl) params.set("imageUrl", imageUrl);
    if (semente !== undefined) params.set("seed", String(semente));

    // ── O TIPO (T7.7), E É A QUINTA VEZ QUE ESTE ÉPICO ENCONTRA O MESMO PADRÃO ────────────
    //
    // Ele, na letra: *"nem entendeu que era um cenário, gerou personagem junto (…) Tá tudo
    // usando a mesma geração."*
    //
    // E era literal. A prateleira aberta já estava aqui do lado — `generate-image.tsx` recebe
    // `category={selectedSubmenu}` e JÁ a usa para decidir se mostra os presets de câmera
    // ("exibidos só para personagens"). O que faltava era ela atravessar a rede: a rota
    // `/generate` não tinha o parâmetro, e o `montarPrompt` tinha uma assinatura só.
    //
    // As quatro ocorrências anteriores do mesmo padrão — botões de camada, `fontFamilies`,
    // `seed` e `refImagem` — estão na lição 25 do `LESSONS.md`.
    if (tipo) params.set("tipo", tipo);
    // T22 · a tag de estilo; sem ela, o estilo da casa.
    if (estilo) params.set("estilo", estilo);

    // `auth?` e nao `auth.`, e a interrogacao foi paga com um defeito medido.
    //
    // `auth` e exportado como `null` quando o Firebase nao esta configurado — o proprio
    // `firebase.class.ts` avisa isso no console e segue. Mas aqui a leitura era direta, entao
    // `auth.currentUser` lancava `TypeError: Cannot read properties of null`, e a geracao de
    // imagem inteira morria antes de tentar. Medido em 2026-09-09 ao ligar a tela do
    // roteirizador: os quatro quadros falharam pelo mesmo TypeError.
    //
    // Nao e um defeito so de desenvolvimento. O backend aceita requisicao ANONIMA — o token
    // serve para rate limit por usuario, nao para autorizar. Entao qualquer situacao em que o
    // Firebase nao suba (chave errada em producao, bloqueio de rede da escola, servico fora)
    // derrubava a geracao com um erro de tipo, em vez de gerar sem o token.
    const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    const headers: HeadersInit = idToken ? { Authorization: `Bearer ${idToken}` } : {};

    const resposta = await fetch(
      `${process.env.REACT_APP_BACKEND_ROUTE}/generate?${params.toString()}`,
      { headers }
    );
    const res = await resposta.json();

    // ══ 422 NÃO É FALHA: É RECUSA, E A DIFERENÇA MUDA O QUE SE DIZ À CRIANÇA ═══════════════════
    //
    // O backend separa as duas coisas de propósito, e diz isso por escrito em `server.ts`:
    // "Recusa de conteúdo é 422 (o pedido é inaceitável), não 500 (o servidor quebrou). A
    // diferença importa: 500 faz o frontend dizer 'tente de novo', e tentar de novo é a pior
    // orientação possível quando um classificador de segurança recusou a imagem."
    //
    // E era exatamente isso que acontecia: este método jogava fora o `message` do corpo, lançava
    // um `Error` genérico, e a tela mostrava `alert("Falha ao gerar imagem. Tente novamente.")`.
    //
    // MEDIDO EM 13/09 contra produção, só pelo código HTTP (o corpo foi descartado):
    //   "um gato gordo"                                  -> 200
    //   "mulher extremamente pelada vista de cima ..."   -> 422
    //   "mulher nua deitada"                             -> 422
    //
    // Ou seja: no dia do teste a barreira SEGUROU. O que o operador viu foi "Falha ao gerar
    // imagem. Tente novamente." — e daí a frase dele no T4.10: "Eu não deveria poder ter isso
    // aqui / Deveria ter um flag de nudez". Do lugar onde ele estava, não havia flag nenhum:
    // havia uma falha genérica. O flag é isto aqui.
    if (resposta.status === 422) {
      throw new ConteudoRecusadoError(
        res?.message ||
          "Esse pedido não pode virar imagem no HQ-Lab. Descreva a cena de outro jeito."
      );
    }

    const { error, images, provider } = res;
    if (error) {
      throw new Error(error);
    }
    if (!images || !Array.isArray(images) || images.length === 0) {
      throw new Error("Nenhuma imagem retornada");
    }

    return { images, provider };
  }

  /**
   * T21 · DESENHAR A PARTIR DE UMA REFERÊNCIA — o FLUX.2 klein, fora da cascata.
   *
   * O quadro 2 em diante de uma história vai com o quadro 1 como referência: é o que mantém o
   * personagem (medido pelo 0049: 2 de 2 pares, contra 0 de 2 do caminho de sempre). Os modos de
   * decupagem usam a mesma porta.
   *
   * Se nenhuma conta do klein responder, o servidor diz 503 e ESTE método lança com a frase dele.
   * Não há reserva em outro modelo, de propósito: *"a consistência é mais importante que qualquer
   * coisa"* (0049 T2.5). Quem chama decide o que dizer à criança.
   */
  static async generateComReferencia(
    prompt: string,
    referencia: string,
    semente?: number,
    modo: "continuar" | "isolar-personagem" | "tirar-personagem" | "separar" | "camadas" | "quadros" | "texto" = "continuar",
    tipo?: string | null,
    personagem?: string,
    estilo?: string,
    /** T26.R4 · uma ficha por personagem do quadro (até 3), com o nome. */
    elenco?: { nome: string; imagem: string }[]
  ): Promise<{ images: string[]; provider: string; nomes?: string[] }> {
    const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    };
    const resposta = await fetch(`${process.env.REACT_APP_BACKEND_ROUTE}/generate-com-referencia`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        prompt: prompt ? enquadramentoEmIngles(prompt) : undefined,
        referencia: referencia || undefined,
        seed: semente,
        personagem: personagem || undefined,
        estilo: estilo || undefined,
        modo,
        tipo: tipo || undefined,
        elenco: elenco?.length ? elenco : undefined,
      }),
    });
    const res = await resposta.json().catch(() => ({}));
    if (resposta.status === 422) {
      throw new ConteudoRecusadoError(
        res?.message || "Esse pedido não pode virar imagem no HQ-Lab. Descreva a cena de outro jeito."
      );
    }
    if (resposta.status === 503) {
      throw new KleinEsgotadoError(res?.message || "O desenhista que mantém o personagem está sem limite agora.");
    }
    if (!resposta.ok) {
      throw new Error(res?.message || res?.error || `HTTP ${resposta.status}`);
    }
    const { images, provider, nomes } = res;
    if (!images || !Array.isArray(images) || images.length === 0) {
      throw new Error("Nenhuma imagem retornada");
    }
    return { images, provider, nomes };
  }
}
